import * as React from 'react';
import { ReactNode, useState } from 'react';
import { Card, CardContent } from '../../../../components/Card';
import { Button } from '../../../../components/Button';
import { Badge } from '../../../../components/Badge';
import { Icon } from '../../../../components/icons/Icon';
import { ErrorSummary } from '../../../../components/errors';
import { Decimal } from '@insio/decimal';
import { cn, combineIssues, severityStyle } from '../../../../utils';
import { useLocale, useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceCurrencyCode,
  useInvoiceIsUnitPriceInclVat,
  useInvoiceIsVatDocument
} from '../../../../providers/InvoiceProvider';
import type { InvoiceLine } from '../../../../modules/modals/EditInvoiceLinesModal';
import type { ValidationIssue } from '../../../../types';
import type { FormatterProps } from '../types/editor-config';
import { InvoiceLineField, InvoiceSection, matchIndexedChild } from '@insio/validation';
import { useFormatAmount, useFormatMoney } from '../../../../hooks';
import { DEFAULT_PLACEHOLDER } from '@insio/format-utils';
import { computeLineItemsGrandTotal, toInvoiceLineItems } from '../utils/invoice-lines';

/** Tolerant Decimal coercion — Apollo decimal-link emits Decimal instances at
 * runtime, but sample data / GraphQL `Decimal: any` may surface as raw numbers
 * or strings. Returns null for nullish/non-numeric inputs. */
function toDecimal(value: unknown): Decimal | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Decimal) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    try {
      const d = new Decimal(value);
      return d.isNaN() ? null : d;
    } catch {
      return null;
    }
  }
  return null;
}

type CellKey =
  'description' | 'quantity' | 'unitPrice' | 'discount' | 'recyclingFee' | 'vat' | 'total';

const FIELD_TO_CELL: Record<string, CellKey> = {
  [InvoiceLineField.description]: 'description',
  [InvoiceLineField.lineNumber]: 'description',
  [InvoiceLineField.quantity]: 'quantity',
  [InvoiceLineField.unit]: 'quantity',
  [InvoiceLineField.unitPrice]: 'unitPrice',
  [InvoiceLineField.unitPriceNoVat]: 'unitPrice',
  [InvoiceLineField.unitPriceInclVat]: 'unitPrice',
  [InvoiceLineField.discountPercent]: 'discount',
  [InvoiceLineField.recyclingFee]: 'recyclingFee',
  [InvoiceLineField.vatRate]: 'vat',
  [InvoiceLineField.vatAmount]: 'vat',
  [InvoiceLineField.totalAmountNoVat]: 'total',
  [InvoiceLineField.totalAmountInclVat]: 'total'
};

interface LineErrors {
  /** Line-level issues (path `invoiceLines.N` without a field, or with an unknown field). */
  line: ValidationIssue[];
  /** Field-level issues grouped by the cell they map to. */
  byCell: Partial<Record<CellKey, ValidationIssue[]>>;
  /** Flat list of every issue belonging to the line — line-level + all cells.
   * Drives the aggregated "errors under the row" rendering. */
  all: ValidationIssue[];
}

function groupLineErrors(errors: ValidationIssue[] | undefined): Map<number, LineErrors> {
  const grouped = new Map<number, LineErrors>();
  if (!errors?.length) return grouped;
  for (const issue of errors) {
    const match = issue.path ? matchIndexedChild(issue.path, InvoiceSection.invoiceLines) : null;

    if (!match) continue;
    let entry = grouped.get(match.index);
    if (!entry) {
      entry = { line: [], byCell: {}, all: [] };
      grouped.set(match.index, entry);
    }
    entry.all.push(issue);
    const cellKey = match.child ? FIELD_TO_CELL[match.child] : undefined;
    if (cellKey) {
      (entry.byCell[cellKey] ??= []).push(issue);
    } else {
      entry.line.push(issue);
    }
  }
  return grouped;
}

export function SubItem({
  children,
  label,
  visible = true,
  className
}: {
  label?: string;
  children: ReactNode;
  visible?: boolean | null;
  className?: string;
}) {
  if (!visible) return null;
  return (
    <div className={cn('text-foreground-muted font-light flex gap-xs text-xs', className)}>
      {label && <span className=''>{label}:</span>}
      <span className=''>{children}</span>
    </div>
  );
}

export function ItemCell({
  children,
  label,
  visible = true,
  className
}: {
  label: string;
  children: ReactNode;
  visible?: boolean | null;
  className?: string;
}) {
  if (!visible) return null;
  return (
    <div className={cn('text-xs', className)}>
      <p className='text-foreground-muted mb-2xs'>{label}</p>
      <p className='text-foreground-base font-medium'>{children}</p>
    </div>
  );
}

/** What a host may draw beside or under one line card. Returning null draws nothing — the default. */
export type InvoiceLineAccessory = (line: Partial<InvoiceLine>, index: number) => React.ReactNode;

interface InvoiceLineFormatterProps {
  line: Partial<InvoiceLine>;
  index: number;
  errors?: LineErrors;
  hasDiscountColumn: boolean;
  renderLineAccessory?: InvoiceLineAccessory;
  renderLineFooter?: InvoiceLineAccessory;
}

export function LineGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('gap-sm grid', className)}>{children}</div>;
}

function InvoiceLineFormatter({
  line,
  index,
  errors,
  hasDiscountColumn,
  renderLineAccessory,
  renderLineFooter
}: InvoiceLineFormatterProps): ReactNode {
  const t = useTranslation('modules.modals.editInvoiceLines');
  const locale = useLocale();
  const currency = useInvoiceCurrencyCode();
  const formatMoney = useFormatMoney(currency);
  const formatAmount = useFormatAmount(currency);

  const unitPrice = toDecimal(line.unitPrice);
  const quantity = toDecimal(line.quantity);
  const discountPercent = toDecimal(line.discountPercent);
  const vatRate = toDecimal(line.vatRate);
  const totalAmountNoVat = toDecimal(line.totalAmountNoVat);
  const vatAmount = toDecimal(line.vatAmount);
  const totalAmountInclVat = toDecimal(line.totalAmountInclVat);
  const recyclingFee = toDecimal(line.recyclingFee);

  const isVatDocument = useInvoiceIsVatDocument();

  const lineSeverity = severityStyle(combineIssues(errors?.all)?.severity);

  const vatDocumentCols = 7; // description, quantity, unit price, vat rate, vat amount, total;
  const nonVatDocumentCols = 5; // description, quantity, unit price, discount, total
  const totalCols =
    (isVatDocument ? vatDocumentCols : nonVatDocumentCols) + (hasDiscountColumn ? 1 : 0);

  const resolveTotalCols = (cols: number): string => {
    switch (cols) {
      case 5:
        return '@md:grid-cols-5';
      case 6:
        return '@md:grid-cols-6';
      case 7:
        return '@lg:grid-cols-7';
      case 8:
        return '@lg:grid-cols-8';
      default:
        return '';
    }
  };

  /** Same content columns, plus an auto-width one for the accessory, so it does not take a
   * full fraction of the row. Kept as literal class names so Tailwind emits them. */
  const resolveAccessoryCols = (cols: number): string => {
    switch (cols) {
      case 5:
        return '@md:grid-cols-[repeat(5,minmax(0,1fr))_auto]';
      case 6:
        return '@md:grid-cols-[repeat(6,minmax(0,1fr))_auto]';
      case 7:
        return '@lg:grid-cols-[repeat(7,minmax(0,1fr))_auto]';
      case 8:
        return '@lg:grid-cols-[repeat(8,minmax(0,1fr))_auto]';
      default:
        return '';
    }
  };

  const totalColKey = isVatDocument ? 'totalAmountNoVat' : 'totalAmount';

  const accessory = renderLineAccessory?.(line, index);
  const hasAccessory = accessory != null && accessory !== false;
  const footer = renderLineFooter?.(line, index);
  const hasFooter = footer != null && footer !== false;

  return (
    <Card
      key={line.id ?? `line-${line.lineNumber ?? index}`}
      className={cn(
        'bg-white border-l-[3px] @container',
        lineSeverity?.borderColor ?? 'border-l-transparent',
        lineSeverity?.bgColor
      )}
    >
      <CardContent
        className={cn(
          'grid grid-cols-1 gap-x-sm gap-y-xs @sm:gap-x-md justify-end',
          hasAccessory ? resolveAccessoryCols(totalCols) : resolveTotalCols(totalCols)
        )}
      >
        <LineGroup className='text-sm'>
          <p className=' text-foreground-base font-normal'>
            {line.description ?? DEFAULT_PLACEHOLDER}
          </p>
          {line.ean || line.code ? (
            <p className=' text-foreground-base font-normal inline-flex gap-sm'>
              {line.ean && <span>{line.ean}</span>}
              {line.code && <span>{line.code}</span>}
            </p>
          ) : null}
        </LineGroup>
        <ItemCell className='' label={t('colQuantity')}>
          {quantity && !quantity.isZero()
            ? `${formatAmount(quantity, { locale, decimalPlaces: 2 })} ${line.unit ?? ''}`
            : DEFAULT_PLACEHOLDER}
        </ItemCell>
        <ItemCell className='' label={t('colUnitPrice')}>
          {formatMoney(unitPrice)}
        </ItemCell>
        <ItemCell className='' visible={hasDiscountColumn} label={t('colDiscount')}>
          {hasDiscountColumn &&
            discountPercent &&
            discountPercent.gt(0) &&
            formatAmount(discountPercent, {
              locale,
              decimalPlaces: 2,
              addPercentSign: true
            })}
        </ItemCell>

        <ItemCell className='' label={t('colVatRate')} visible={isVatDocument}>
          {vatRate &&
            vatRate.gt(0) &&
            formatAmount(vatRate, {
              locale,
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
              addPercentSign: true
            })}
        </ItemCell>

        <ItemCell className='' label={t('colVatTotal')} visible={isVatDocument}>
          {formatMoney(vatAmount)}
        </ItemCell>
        <ItemCell className='' label={t(totalColKey)}>
          {formatMoney(totalAmountNoVat)}
        </ItemCell>
        <ItemCell className='font-bold' label={t('colTotal')} visible={isVatDocument}>
          {formatMoney(totalAmountInclVat)}
        </ItemCell>
        {hasAccessory && <div className='flex items-center justify-end'>{accessory}</div>}
        <SubItem
          className='col-span-full'
          label={t('colRecyclingFee')}
          visible={recyclingFee && !recyclingFee.isZero}
        >
          {formatMoney(recyclingFee)}
        </SubItem>

        {errors && errors.all.length > 0 && (
          <ErrorSummary
            issues={errors.all}
            className='col-span-full pt-sm border-t border-border-base'
          />
        )}
        {hasFooter && <div className='col-span-full min-w-0'>{footer}</div>}
      </CardContent>
    </Card>
  );
}

export interface InvoiceLinesFormatterProps extends FormatterProps {
  /** Drawn as the last cell of each line card, after the total. A host supplying an interactive
   * node must stop
   * propagation — the whole display is one role='button' that opens the edit modal. */
  renderLineAccessory?: InvoiceLineAccessory;
  /** Drawn full width at the bottom of each line card. Same propagation rule as the accessory. */
  renderLineFooter?: InvoiceLineAccessory;
}

/** Read-only display of an invoice-lines value: collapsible list of line cards. */
export function InvoiceLinesFormatter({
  value,
  errors,
  renderLineAccessory,
  renderLineFooter
}: InvoiceLinesFormatterProps): ReactNode {
  const t = useTranslation('modules.modals.editInvoiceLines');
  const isUnitPriceInclVat = useInvoiceIsUnitPriceInclVat();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const [isExpanded, setIsExpanded] = useState(true);
  const formatMoney = useFormatMoney(invoiceCurrencyCode);
  const isVatDocument = useInvoiceIsVatDocument();

  // Tolerant of both shapes the pipeline hands over: a plain line array or the
  // committed `{ invoiceLines, isUnitPriceInclVat }` save payload.
  const invoiceLines = toInvoiceLineItems(value);
  const errorsByLine = groupLineErrors(errors);
  // VAT docs sum the gross per-line total, non-VAT docs the net one — matching
  // the per-line display. `null` when incomplete, so we render nothing (not 0).
  const grandTotal = computeLineItemsGrandTotal(invoiceLines, isVatDocument);

  const hasDiscountColumn = invoiceLines.some(line => {
    const d = toDecimal(line.discountPercent);
    return d !== null && d.gt(0);
  });

  return (
    <div className='w-full rounded-xl bg-white p-sm'>
      <div className='mb-sm'>
        <div className='flex items-start justify-between md:items-center'>
          <div className='flex flex-col items-start gap-xs md:flex-row md:items-center md:gap-lg'>
            <Button
              variant='ghost'
              size='sm'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // Don't bubble to the EditableFieldDisplay wrapper — otherwise
                // toggling collapse/expand also opens the edit modal.
                e.stopPropagation();
                setIsExpanded(prev => !prev);
              }}
              className='flex items-center gap-sm px-0 hover:bg-transparent'
              aria-expanded={isExpanded}
              aria-label={t('sectionTitle')}
            >
              <span className='text-sm font-semibold text-foreground-base'>
                {t('sectionTitle')}
                {invoiceLines.length > 0 && <span className='ml-xs'>({invoiceLines.length})</span>}
              </span>
            </Button>
            {isVatDocument && (
              <div className='flex items-center gap-xs'>
                <span
                  className={cn(
                    'text-xs font-medium whitespace-nowrap',
                    isUnitPriceInclVat ? 'text-info-text' : 'text-foreground-muted'
                  )}
                >
                  {t('unitPricesLabel')}
                </span>
                <Badge
                  size='sm'
                  color={isUnitPriceInclVat ? 'info' : 'neutral'}
                  className='h-auto px-xs py-2xs text-2xs border'
                >
                  {isUnitPriceInclVat ? t('unitPricesInclVat') : t('unitPricesExclVat')}
                </Badge>
              </div>
            )}
          </div>

          <div className='flex items-center gap-sm'>
            {grandTotal !== null && (
              <span className='hidden md:inline text-sm font-bold text-foreground-base whitespace-nowrap'>
                {formatMoney(grandTotal)}
              </span>
            )}
            <Button
              variant='ghost'
              size='sm'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // Don't bubble to the EditableFieldDisplay wrapper — otherwise
                // toggling collapse/expand also opens the edit modal.
                e.stopPropagation();
                setIsExpanded(prev => !prev);
              }}
              className='px-0 hover:bg-transparent'
              aria-hidden='true'
              tabIndex={-1}
            >
              <Icon
                name='chevron-down'
                className={`h-4 w-4 text-foreground-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
        </div>

        {grandTotal !== null && (
          <div className='md:hidden pl-xs mt-2xs'>
            <span className='text-sm font-bold text-foreground-base whitespace-nowrap'>
              {formatMoney(grandTotal)}
            </span>
          </div>
        )}
      </div>

      {isExpanded && (
        <>
          {invoiceLines.length > 0 ? (
            <>
              <div className='space-y-sm'>
                {invoiceLines.map((line, index) => (
                  <InvoiceLineFormatter
                    key={line.id ?? `line-${line.lineNumber ?? index}`}
                    line={line}
                    index={index}
                    errors={errorsByLine.get(index)}
                    hasDiscountColumn={hasDiscountColumn}
                    renderLineAccessory={renderLineAccessory}
                    renderLineFooter={renderLineFooter}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className='text-sm text-foreground-muted text-center py-md'>{t('noLines')}</p>
          )}
        </>
      )}
    </div>
  );
}
